import { useState, type ReactNode } from 'react';

import { useAuth } from '../../state/auth';
import { useFigmaBreakpoint } from '../../figma/useFigmaBreakpoint';
import { handleNavClick, helpUrl, MAIN_NAV_ITEMS } from '../../config/navigation';
import { Button } from '../Button/Button';
import { Logo, LogoArtwork } from '../Logo/Logo';
import { MobileDrawer, type MobileDrawerAction, type MobileDrawerVariant } from '../MobileDrawer/MobileDrawer';
import { LanguageSwitch } from '../../i18n/LanguageSwitch';
import { useScrollHeader } from './useScrollHeader';
import './header.css';

export interface HeaderProps {
  authenticated?: boolean;
  account?: ReactNode;
  actions?: ReactNode;
  mobileActions?: ReactNode;
  navigation?: ReactNode;
  onLogoClick?: () => void;
  onLogin?: () => void;
  onCarrierRegistration?: () => void;
  onMenuToggle?: () => void;
  menuExpanded?: boolean;
  externalDrawer?: boolean;
  drawerVariant?: MobileDrawerVariant;
  activeDrawerAction?: MobileDrawerAction;
  onDrawerNavigate?: (action: MobileDrawerAction) => void;
}

export function Header({
  authenticated,
  account,
  actions,
  mobileActions,
  navigation,
  onLogoClick,
  onLogin,
  onCarrierRegistration,
  onMenuToggle,
  menuExpanded,
  externalDrawer = false,
  drawerVariant,
  activeDrawerAction,
  onDrawerNavigate,
}: HeaderProps) {
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const isMenuOpen = menuExpanded ?? internalMenuOpen;
  const scrollHeader = useScrollHeader(isMenuOpen);

  const toggleMenu = () => {
    if (onMenuToggle) {
      onMenuToggle();
    } else {
      setInternalMenuOpen((open) => !open);
    }
  };

  const closeMenu = () => {
    if (onMenuToggle && menuExpanded) {
      onMenuToggle();
    } else {
      setInternalMenuOpen(false);
    }
  };

  const { user, status } = useAuth();
  const isAuth = authenticated ?? (status === 'authenticated' && Boolean(user));
  const isCarrier = user?.active_role === 'carrier';
  const authenticatedHelpHash = helpUrl(isAuth ? user?.active_role : null);
  const defaultDisplayName = user?.display_name || user?.email || (isCarrier ? 'Кабинет перевозчика' : 'Личный кабинет');
  const defaultCabinetUrl = isCarrier ? '/portal/#carrier-dashboard' : '/portal/#my-requests';
  const computedDrawerVariant: MobileDrawerVariant = drawerVariant ?? (isAuth ? (isCarrier ? 'carrier' : 'owner') : 'no log in');
  const breakpoint = useFigmaBreakpoint();
  const node = isAuth
    ? { '1920': '61:21901', '1440': '124:35934', '768': '124:35445', '375': '124:35746' }[breakpoint]
    : { '1920': '61:21813', '1440': '124:35910', '768': '124:35387', '375': '124:35648' }[breakpoint];

  return (
    <>
    {scrollHeader.floating ? <div aria-hidden="true" style={{ height: scrollHeader.height, flexShrink: 0 }} /> : null}
    <header
      ref={scrollHeader.ref}
      className={`lovo-header${scrollHeader.floating ? ' lovo-header--floating' : ''}${scrollHeader.floating && !scrollHeader.visible ? ' lovo-header--scroll-hidden' : ''}`}
      data-figma-node={node}
      data-figma-variant={`State=${isAuth ? 'Logged in v2' : 'No log in'}, Size=${breakpoint}`}
    >
      <div className="lovo-header__left" data-figma-node="61:21734">
        {window.location.hash && window.location.hash !== '#landing' && window.location.hash !== '#top' ? (
          <a
            href="#landing"
            className="lovo-logo"
            aria-label="LOVO"
            onClick={(e) => {
              if (onLogoClick) {
                e.preventDefault();
                onLogoClick();
              }
            }}
          >
            <LogoArtwork tone="dark" />
          </a>
        ) : (
          <Logo onClick={onLogoClick} />
        )}
        <nav className="lovo-header__nav" aria-label="Основная навигация" data-figma-node="61:21741">
          {navigation ?? (
            MAIN_NAV_ITEMS.map((item) => (
              <a
                key={item.id}
                href={item.id === 'help' ? authenticatedHelpHash : (item.href || undefined)}
                className={item.disabled && !(item.id === 'help' && authenticatedHelpHash) ? 'is-disabled' : undefined}
                onClick={(event) => {
                  if (item.id === 'help' && authenticatedHelpHash) return;
                  handleNavClick(item, undefined, event);
                }}
              >
                {item.label}
              </a>
            ))
          )}
        </nav>
      </div>

      <div
        className={`lovo-header__account${actions ? ' lovo-header__account--with-actions' : ''}`}
        data-figma-node="61:21746"
      >
        <LanguageSwitch />
        {isAuth ? (
          <>
            {actions}
            {account ?? (
              <a
                className="lovo-header__account-btn"
                href={defaultCabinetUrl}
              >
                <span>{defaultDisplayName}</span>
                <img src="/figma/assets/profile-arrow-up-right.svg" alt="" aria-hidden="true" />
              </a>
            )}
          </>
        ) : (
          <>
            <a
              className="lovo-header__link"
              href="/portal/#carrier-rega-email"
              onClick={(e) => {
                if (onCarrierRegistration) {
                  e.preventDefault();
                  onCarrierRegistration();
                }
              }}
            >
              Стать перевозчиком
            </a>
            <a
              className="lovo-button lovo-button--primary lovo-button--medium lovo-header__login"
              href="/portal/#login"
              onClick={(e) => {
                if (onLogin) {
                  e.preventDefault();
                  onLogin();
                }
              }}
            >
              <span className="lovo-button__label">Войти</span>
              <img
                className="lovo-button__arrow lovo-button__arrow--up-right"
                src="/figma/assets/landing-arrow-white.svg"
                alt=""
                aria-hidden="true"
              />
            </a>
          </>
        )}
      </div>

      <div className="lovo-header__mobile-tools">
        {mobileActions ?? (isAuth ? actions : null)}
        <LanguageSwitch />
        <button
          className="lovo-header__menu"
          type="button"
          aria-label="Открыть меню"
          aria-expanded={isMenuOpen}
          onClick={toggleMenu}
          data-figma-node="124:35356"
        >
          <img src="/figma/assets/menu.svg" alt="" aria-hidden="true" />
        </button>
      </div>

      {isMenuOpen && !externalDrawer ? (
        <MobileDrawer
          variant={computedDrawerVariant}
          activeAction={activeDrawerAction}
          onClose={closeMenu}
          onLogoClick={() => {
            closeMenu();
            if (onLogoClick) onLogoClick();
            else window.location.hash = '#landing';
          }}
          onNavigate={(action) => {
            closeMenu();
            if (onDrawerNavigate) {
              onDrawerNavigate(action);
            } else {
              if (action === 'help') {
                window.location.assign(helpUrl(isAuth ? user?.active_role : null));
              } else if (action === 'carrier-help') {
                window.location.hash = '#carrier-help';
              } else if (action === 'logo' || action === 'how-it-works' || action === 'why-lovo' || action === 'reviews') {
                const item = MAIN_NAV_ITEMS.find((entry) => entry.id === action);
                if (item) handleNavClick(item);
                else window.location.hash = '#landing';
              } else if (action === 'login') {
                window.location.hash = '#login';
              } else if (action === 'carrier-registration') {
                window.location.hash = '#carrier-rega-email';
              } else if (action === 'personal-cabinet' || action === 'owner-requests') {
                window.location.hash = '#my-requests';
              } else if (action === 'carrier-dashboard' || action === 'carrier-my-offers') {
                window.location.hash = '#carrier-dashboard';
              }
            }
          }}
        />
      ) : null}
    </header>
    </>
  );
}
